import ResidentData from "./residentData";
import BookingNotes from "./bookingNotes";
import {EventBookingStatus} from "../../../../shared/enums/eventBookingStatus";
import {makeAutoObservable} from "mobx";
import {EventBookingSource} from "../../../../shared/enums/eventBookingSource";

export default class EventBooking {
  id: string;
  index: number;
  source: EventBookingSource;
  residentData: ResidentData;
  notes: BookingNotes;
  status: EventBookingStatus;
  isPaid?: boolean;
  isPaidByFee?: boolean;
  personId: string;
  guestCount: number;
  creatorId: string;
  creatorName?: string;
  concurrency?: string;
  /**
   * We sometimes add "generated rows" to the bookings tables.
   * These are rows which allow the user to add a resident, show reserved capacity, or show empty capacity.
   *
   * These flags help identify the intention of those rows.
   */

  /**
   * This row is added for "add resident" functionality.
   */
  isAddResident: boolean = false;
  /**
   * This row represents empty capacity, which only staff can book
   */
  isReservedRow: boolean = false;
  /**
   * This row represents empty capacity, which anyone can book
   */
  isCapacityRow: boolean = false;

  /**
   * This row represents a booking which exists in the system, but is still to be loaded
   */
  isLoadingResidentDetails: boolean = false;

  /**
   * Should this booking show a loader in the status column
   */
  isStatusSpinning: boolean = false;

  /**
   * Should this booking show a shimmer in the status column
   */
  isStatusShimmer: boolean = false;

  /**
   * Should this booking show a loader in the source column
   */
  isSourceSpinning: boolean = false;

  /**
   * Should this booking show a shimmer in the source column
   */
  isSourceShimmer: boolean = false;

  readonly isReadOnly: boolean;

  /**
   * When a booking has been added for display purposes only, and does not actually exist in the system
   */
  get isGeneratedRow(): boolean {
    return this.isAddResident || this.isReservedRow || this.isCapacityRow;
  }

  constructor(
    id: string,
    index: number,
    source: EventBookingSource,
    status: EventBookingStatus,
    residentData: ResidentData,
    notes: BookingNotes,
    personId: string,
    creatorId: string,
    isPaid?: boolean,
    guestCount: number = 1,
    isReadOnly: boolean = false,
    creatorName?: string,
    concurrency?: string,
    isPaidByFee?: boolean
  ) {
    makeAutoObservable(this);

    this.id = id;
    this.index = index;
    this.source = source;
    this.residentData = residentData;
    this.notes = notes;
    this.status = status;
    this.personId = personId;
    this.isPaid = isPaid;
    this.isPaidByFee = isPaidByFee;
    this.guestCount = guestCount;
    this.creatorId = creatorId;
    this.creatorName = creatorName;
    this.concurrency = concurrency;
    this.isReadOnly = isReadOnly;
  }

  get requestObject() {
    return {
      id: this.id,
      source: this.source,
      residentData: {
        name: this.residentData.name,
        unit: this.residentData.unit,
        phoneNumber: this.residentData.phoneNumber,
        unitId: this.residentData.unitId,
        unitName: this.residentData.unitName,
        unitCode: this.residentData.unitCode
      },
      notes: {
        residentNote: this.notes.residentNote,
        staffNote: this.notes.staffNote
      },
      status: this.status,
      isPaid:
        this.status === EventBookingStatus.Waitlist ? undefined : this.isPaid,
      personId: this.personId,
      guestCount: this.guestCount,
      creatorId: this.creatorId,
      concurrency: this.concurrency
    };
  }
}
